import { defineComponent as _defineComponent } from 'vue'
import { ref, inject, computed, onActivated } from "vue";
import Page from "@/components/shared/Page.vue";
import NavigationBar from "@/components/shared/NavigationBar.vue";
import {
  LostFoundType,
  LostFoundTypeCode,
  PROVIDE_KEY_REMOVE_LOADING_IMAGE,
  PROVIDE_KEY_SHOW_ERROR_MODAL,
  PROVIDE_KEY_SHOW_LOADING_IMAGE,
  RemoveLoadingImage,
  RouterName,
  ShowErrorModal,
  ShowLoadingImage,
} from "@/types";
import SearchInput from "@/components/shared/inputs/SearchInput.vue";
import router from "@/router";
import InlineSvg from "vue-inline-svg";
import Modal from "@/components/shared/modal/base/Modal.vue";
import { useBbsStore } from "@/stores/bbsStore";
import SegmentedControl from "@/components/shared/SegmentedControl.vue";
import { ILostFound } from "@/domain/entities/bbs/lostFound/lostFound.types";
import LostList from "@/components/bbs/lostFound/LostList.vue";
import FoundList from "@/components/bbs/lostFound/FoundList.vue";
import LostFoundGuide from "@/components/bbs/lostFound/LostFoundGuide.vue";
import { useServiceStore } from "@/stores/serviceStore";
import ScrollTopControl from "@/components/shared/ScrollTopControl.vue";
import { useScrollToTop } from "@/composables/shared/useScrollToTop";
import PageModal from "@/components/shared/modal/base/PageModal.vue";


const __default__ = {
  name: "LostFoundList",
};

export default /*#__PURE__*/_defineComponent({
  ...__default__,
  setup(__props, { expose: __expose }) {
  __expose();

const showErrorModal = inject(PROVIDE_KEY_SHOW_ERROR_MODAL) as ShowErrorModal;
const showLoadingImage = inject(
  PROVIDE_KEY_SHOW_LOADING_IMAGE
) as ShowLoadingImage;
const removeLoadingImage = inject(
  PROVIDE_KEY_REMOVE_LOADING_IMAGE
) as RemoveLoadingImage;

const serviceStore = useServiceStore();
const bbsStore = useBbsStore();

const lostAndFoundGuideUrl = computed(
  () => serviceStore.serviceInfo?.lostAndFoundGuideUrl ?? ""
);

const segments = [
  {
    title: "습득물",
    type: LostFoundTypeCode.Found,
  },
  {
    title: "분실물",
    type: LostFoundTypeCode.Lost,
  },
];
const segmentTitles = segments.map((segment) => segment.title);

const selectedSegmentIndex = ref(0);
const selectedSegment = computed(() => segments[selectedSegmentIndex.value]);

const getSegmentIndex = (type: LostFoundType) =>
  segments.findIndex((segment) => segment.type === type);

const updateSegmentIndex = (index: number) => {
  if (selectedSegmentIndex.value === index) return;
  selectedSegmentIndex.value = index;
  resetAndGetLostFoundList();
};

// 하루 작성 횟수 초과 모달 처리
const maxDailyLostFoundCreationCount =
  serviceStore.serviceInfo?.maxDailyLostFoundCreationCount;

const shouldShowDailyWritingCountLimitModal = ref(false);
const toggleShowDailyWritingCountLimitModal = (show: boolean) => {
  shouldShowDailyWritingCountLimitModal.value = show;
};

const onClickLostCreateBtn = async () => {
  showLoadingImage();
  // 오늘 작성한 본인 글 목록을 조회
  const listResult = await bbsStore.getLostFoundList(
    LostFoundTypeCode.Lost,
    "",
    0,
    undefined,
    true
  );
  removeLoadingImage();

  const totalCount = listResult.data?.totalCount ?? 0;
  const isExceeded = maxDailyLostFoundCreationCount
    ? totalCount >= maxDailyLostFoundCreationCount
    : false;

  if (isExceeded) {
    toggleShowDailyWritingCountLimitModal(true);
  } else {
    goToLostFoundEdit();
  }
};

const shouldShowLostFoundGuide = ref(false);
const toggleShowLostFoundGuide = (show: boolean) => {
  shouldShowLostFoundGuide.value = show;
};

const goToLostFoundEdit = () => {
  router.push({ name: RouterName.LostCreate });
};

const goToLostOrFoundDetail = (lostFound: ILostFound) => {
  const routerName =
    lostFound.type === LostFoundTypeCode.Lost
      ? RouterName.LostDetail
      : RouterName.FoundDetail;

  router.push({
    name: routerName,
    params: { uid: lostFound.uid },
  });
};

const isLoading = ref(false);

const currentPage = ref(0);
const currentLimit = 20;
const totalCount = ref(0);

const foundList = ref<ILostFound[]>([]);
const lostList = ref<ILostFound[]>([]);

const currentList = computed(() =>
  selectedSegment.value.type === LostFoundTypeCode.Lost ? lostList : foundList
);

const getLostFoundList = async () => {
  // 이미 로딩 중이면 중복 호출 방지
  if (isLoading.value) return;

  isLoading.value = true;
  const currentOffset = currentPage.value * currentLimit;
  const listResult = await bbsStore.getLostFoundList(
    selectedSegment.value.type,
    searchKeyword.value,
    currentOffset,
    currentLimit
  );
  isLoading.value = false;

  if (!listResult.isSuccess) {
    showErrorModal(
      `${selectedSegment.value.title} 목록 오류`,
      listResult.errorMessage,
      listResult.resultCode,
      listResult.errorTime
    );
    return;
  }

  if (!listResult.data) return;

  totalCount.value = listResult.data.totalCount;
  if (listResult.data.list.length === 0) return;

  currentList.value.value = [
    ...currentList.value.value,
    ...listResult.data.list,
  ];
  currentPage.value += 1;
};

// 검색
const searchKeyword = ref("");

const clearSearchKeyword = () => {
  searchKeyword.value = "";
};

const resetAndGetLostFoundList = async () => {
  showLoadingImage();
  currentPage.value = 0;
  currentList.value.value = [];
  await getLostFoundList();
  removeLoadingImage();
};

const listContainer = ref();

const handleScroll = async () => {
  if (!listContainer.value) return;

  const nearBottom =
    listContainer.value.clientHeight + listContainer.value.scrollTop >
    listContainer.value.scrollHeight - 200;

  if (!nearBottom) return;
  if (currentList.value.value.length >= totalCount.value) return;
  await getLostFoundList();
};

onActivated(async () => {
  // 분실물 등록/수정/삭제 후 목록 화면으로 돌아왔을때 currentPage, applications 초기화 필요
  await resetAndGetLostFoundList();
});

const __returned__ = { showErrorModal, showLoadingImage, removeLoadingImage, serviceStore, bbsStore, lostAndFoundGuideUrl, segments, segmentTitles, selectedSegmentIndex, selectedSegment, getSegmentIndex, updateSegmentIndex, maxDailyLostFoundCreationCount, shouldShowDailyWritingCountLimitModal, toggleShowDailyWritingCountLimitModal, onClickLostCreateBtn, shouldShowLostFoundGuide, toggleShowLostFoundGuide, goToLostFoundEdit, goToLostOrFoundDetail, isLoading, currentPage, currentLimit, totalCount, foundList, lostList, currentList, getLostFoundList, searchKeyword, clearSearchKeyword, resetAndGetLostFoundList, listContainer, handleScroll, Page, NavigationBar, get LostFoundTypeCode() { return LostFoundTypeCode }, SearchInput, get InlineSvg() { return InlineSvg }, Modal, SegmentedControl, LostList, FoundList, LostFoundGuide, ScrollTopControl, PageModal }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})